/**
 * AbsenceBlock component for displaying absences in the shift calendar
 * Renders absence cards in week view and absence bars in day view
 */

import { useLingui } from '@lingui/react';
import { cn } from '@/lib/utils';
import type { StatusTone } from '@/components/ui/status-badge';
import type { EmployeeAbsence, AbsenceTypeValue } from '@/types/employeeAbsences';
import { ABSENCE_TYPE_TONES, getAbsenceTypeLabel } from '@/components/absences/AbsenceTypeBadge';

// Day view hours (6:00 to 22:00) - must match ShiftCalendarGrid
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;

/** Calendar block colours per status tone; the same tone map as the absence type badge. */
const TONE_BLOCK_CLASSES: Record<StatusTone, { bgClass: string; borderClass: string; textClass: string; hoverClass: string }> = {
  info: {
    bgClass: 'bg-info-muted/60',
    borderClass: 'border-info-on-surface/40 border-l-info-on-surface',
    textClass: 'text-info-on-surface',
    hoverClass: 'hover:bg-info-muted',
  },
  danger: {
    bgClass: 'bg-destructive-muted/60',
    borderClass: 'border-destructive-on-surface/40 border-l-destructive-on-surface',
    textClass: 'text-destructive-on-surface',
    hoverClass: 'hover:bg-destructive-muted',
  },
  warning: {
    bgClass: 'bg-warning-muted/60',
    borderClass: 'border-warning-on-surface/40 border-l-warning-on-surface',
    textClass: 'text-warning-on-surface',
    hoverClass: 'hover:bg-warning-muted',
  },
  success: {
    bgClass: 'bg-success-muted/60',
    borderClass: 'border-success-on-surface/40 border-l-success-on-surface',
    textClass: 'text-success-on-surface',
    hoverClass: 'hover:bg-success-muted',
  },
  accent: {
    bgClass: 'bg-primary/5',
    borderClass: 'border-primary/40 border-l-primary',
    textClass: 'text-foreground',
    hoverClass: 'hover:bg-primary/10',
  },
  neutral: {
    bgClass: 'bg-muted/60',
    borderClass: 'border-border border-l-muted-foreground',
    textClass: 'text-muted-foreground',
    hoverClass: 'hover:bg-muted',
  },
};

/**
 * Colour classes and icon for an absence type, from the badge's tone map so a
 * vacation looks the same in the calendar and in the lists.
 */
export function getAbsenceTypeConfig(type: AbsenceTypeValue) {
  const { tone, icon } = ABSENCE_TYPE_TONES[type] ?? { tone: 'neutral' as const };
  return { ...TONE_BLOCK_CLASSES[tone], icon };
}

interface AbsenceCardProps {
  absence: EmployeeAbsence;
  onClick?: (absence: EmployeeAbsence) => void;
}

/**
 * AbsenceCard - displayed in week view grid cells
 * Shows a compact card with absence type
 */
export function AbsenceCard({ absence, onClick }: AbsenceCardProps) {
  const { i18n } = useLingui();
  const config = getAbsenceTypeConfig(absence.absence_type as AbsenceTypeValue);
  const Icon = config.icon;
  const label = getAbsenceTypeLabel(absence.absence_type as AbsenceTypeValue, i18n);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(absence);
  };

  // Build tooltip with details
  const tooltipParts = [label];
  if (!absence.full_day && absence.from_time && absence.to_time) {
    tooltipParts.push(`${absence.from_time} - ${absence.to_time}`);
  }
  if (absence.notes) {
    tooltipParts.push(absence.notes);
  }
  const tooltip = tooltipParts.join('\n');

  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip}
      className={cn(
        'w-full text-left p-2 rounded-md text-xs transition-colors',
        'border border-l-4 border-dashed',
        'cursor-pointer',
        config.bgClass,
        config.borderClass,
        config.textClass,
        config.hoverClass
      )}
      aria-label={i18n._({ id: 'absences.type.ariaLabel', message: 'Absence type: {type}', values: { type: label } })}
    >
      <div className="flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
        <span className="font-medium truncate">{label}</span>
      </div>
      {!absence.full_day && absence.from_time && absence.to_time && (
        <div className="truncate opacity-75">
          {absence.from_time} - {absence.to_time}
        </div>
      )}
    </button>
  );
}

interface AbsenceBarProps {
  absence: EmployeeAbsence;
  onClick?: (absence: EmployeeAbsence) => void;
  totalHours: number;
}

/**
 * AbsenceBar - displayed in day view timeline
 * Shows a horizontal bar spanning the absence duration
 */
export function AbsenceBar({ absence, onClick, totalHours }: AbsenceBarProps) {
  const { i18n } = useLingui();
  const config = getAbsenceTypeConfig(absence.absence_type as AbsenceTypeValue);
  const Icon = config.icon;
  const label = getAbsenceTypeLabel(absence.absence_type as AbsenceTypeValue, i18n);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(absence);
  };

  // Calculate position based on time
  let startHour = DAY_START_HOUR;
  let endHour = DAY_END_HOUR;

  if (!absence.full_day && absence.from_time && absence.to_time) {
    // Parse HH:mm format
    const [fromHours, fromMinutes] = absence.from_time.split(':').map(Number);
    const [toHours, toMinutes] = absence.to_time.split(':').map(Number);
    startHour = fromHours + (fromMinutes || 0) / 60;
    endHour = toHours + (toMinutes || 0) / 60;

    // Clamp to display range
    startHour = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR, startHour));
    endHour = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR, endHour));
  }

  const startPercent = ((startHour - DAY_START_HOUR) / totalHours) * 100;
  const widthPercent = ((endHour - startHour) / totalHours) * 100;

  // Build tooltip
  const tooltipParts = [label];
  if (!absence.full_day && absence.from_time && absence.to_time) {
    tooltipParts.push(`${absence.from_time} - ${absence.to_time}`);
  } else {
    tooltipParts.push(i18n._({ id: 'absences.form.fullDay', message: 'Full Day' }));
  }
  if (absence.notes) {
    tooltipParts.push(absence.notes);
  }
  const tooltip = tooltipParts.join('\n');

  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip}
      className={cn(
        'absolute h-8 top-1/2 -translate-y-1/2 rounded-md text-xs transition-colors',
        'cursor-pointer flex items-center gap-2 px-2 overflow-hidden',
        'border border-l-4 border-dashed',
        config.bgClass,
        config.borderClass,
        config.textClass,
        config.hoverClass
      )}
      style={{
        left: `${startPercent}%`,
        width: `${widthPercent}%`,
        minWidth: '40px',
        zIndex: 1,
      }}
      aria-label={i18n._({ id: 'absences.type.ariaLabel', message: 'Absence type: {type}', values: { type: label } })}
    >
      {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span className="truncate font-medium">{label}</span>
      {!absence.full_day && absence.from_time && absence.to_time && (
        <span className="truncate opacity-75">
          {absence.from_time} - {absence.to_time}
        </span>
      )}
    </button>
  );
}
